import { create } from 'zustand';
import { domToPng } from 'modern-screenshot';
import { showNotification } from '../services/notificationService';

/**
 * A screenshot captured from the current page, held in memory until it is
 * consumed by the Report Issue form (which uploads it and then clears the store).
 */
export interface CapturedScreenshot {
  id: string;
  /** PNG data URL produced by modern-screenshot's domToPng. */
  dataUrl: string;
  /** The page URL the shot was taken on (shown in the ticket / stored on it). */
  pageUrl: string;
  capturedAt: number;
}

/** Maximum number of screenshots that can be captured/attached at a time.
 *  Kept in sync with the server upload cap (UploadCtrl.ts MAX_FILES = 5). */
export const MAX_SCREENSHOTS = 5;

interface ScreenshotState {
  screenshots: CapturedScreenshot[];
  /** Adds a screenshot; returns false (and warns) when the 5-shot limit is reached. */
  addScreenshot: (dataUrl: string, pageUrl?: string) => boolean;
  /** True once MAX_SCREENSHOTS have been captured. */
  isAtLimit: () => boolean;
  removeScreenshot: (id: string) => void;
  clearScreenshots: () => void;
  /**
   * Capture the current page (document.body) via modern-screenshot and push the
   * resulting PNG onto the screenshots array. Elements flagged with
   * `data-html2canvas-ignore="true"` (our own floating widgets / overlays) are
   * excluded from the capture.
   */
  takeScreenshots: () => Promise<void>;
  /** Shared "New Ticket" popup flag — lets the floating Report Issue button open
   *  the create-ticket form as a global overlay from ANY page. */
  ticketModalOpen: boolean;
  openTicketModal: () => void;
  closeTicketModal: () => void;
  /** Bumped after a ticket is successfully created. The create form is a global
   *  overlay portalled outside the tickets page, so screens that list tickets
   *  watch this counter to know when to refetch. */
  ticketsVersion: number;
  bumpTicketsVersion: () => void;
}

/**
 * Global store for page screenshots + the global "new ticket" modal flag.
 *
 * Screenshots are kept in memory until the Report Issue form consumes them.
 * Uses modern-screenshot (SVG <foreignObject> + the browser's own engine), so it
 * handles modern CSS (color-mix(), oklch(), …) that html2canvas cannot parse.
 */
export const useScreenshotStore = create<ScreenshotState>((set, get) => {
  /** Appends a shot, enforcing the MAX_SCREENSHOTS cap. Returns whether it was added. */
  const pushShot = (dataUrl: string, pageUrl: string): boolean => {
    if (get().screenshots.length >= MAX_SCREENSHOTS) {
      showNotification('warning', `You can attach up to ${MAX_SCREENSHOTS} screenshots`, 2500);
      return false;
    }
    const capturedAt = Date.now();
    const id = `${capturedAt}-${Math.random().toString(36).slice(2, 8)}`;
    set((state) => ({
      screenshots: [...state.screenshots, { id, dataUrl, pageUrl, capturedAt }],
    }));
    return true;
  };

  return {
    screenshots: [],

    addScreenshot: (dataUrl, pageUrl) => pushShot(dataUrl, pageUrl ?? window.location.href),

    isAtLimit: () => get().screenshots.length >= MAX_SCREENSHOTS,

    removeScreenshot: (id) =>
      set((state) => ({ screenshots: state.screenshots.filter((s) => s.id !== id) })),

    clearScreenshots: () => set({ screenshots: [] }),

    takeScreenshots: async () => {
      // Stop before the (slow) capture if we're already at the limit.
      if (get().screenshots.length >= MAX_SCREENSHOTS) {
        showNotification('warning', `You can attach up to ${MAX_SCREENSHOTS} screenshots`, 2500);
        return;
      }
      // Immediate feedback on click — the capture below is async and can take a
      // moment, so we don't wait for it to tell the user something happened.
      showNotification('info', 'Capturing screenshot…', 1200);
      try {
        const dataUrl = await domToPng(document.body, {
          scale: window.devicePixelRatio || 1,
          filter: (node: Node) =>
            !(node instanceof HTMLElement && node.getAttribute('data-html2canvas-ignore') === 'true'),
        });
        if (pushShot(dataUrl, window.location.href)) {
          showNotification('success', `Screenshot captured (${get().screenshots.length}/${MAX_SCREENSHOTS})`, 1500);
        }
      } catch (err) {
        console.error('Screenshot capture failed:', err);
        showNotification('error', 'Failed to capture screenshot', 2000);
      }
    },

    ticketModalOpen: false,
    openTicketModal: () => set({ ticketModalOpen: true }),
    closeTicketModal: () => set({ ticketModalOpen: false }),

    ticketsVersion: 0,
    bumpTicketsVersion: () => set((state) => ({ ticketsVersion: state.ticketsVersion + 1 })),
  };
});
